
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { handleForgotPassword } from "./AuthUtils";
import { Eye, EyeOff } from "lucide-react";

interface ResetPasswordFormProps {
  switchToMode: (mode: string) => void;
  email: string;
  setEmail: (email: string) => void;
}

const ResetPasswordForm = ({ switchToMode, email, setEmail }: ResetPasswordFormProps) => {
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const handleResetPassword = async () => {
    if (cooldown > 0) return;
    
    setLoading(true);
    const success = await handleForgotPassword(email);
    setResetSent(success);
    setLoading(false);
    
    if (success) {
      // Set a 60 second cooldown to prevent spam
      setCooldown(60);
      const interval = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  return (
    <div className="space-y-4">
      {!resetSent ? (
        <>
          <div className="space-y-2">
            <Label htmlFor="reset-email">Email</Label>
            <Input
              id="reset-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="hello@example.com"
              className="h-12"
              required
            />
          </div>
          <p className="text-sm text-clustr-darkGray">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </>
      ) : (
        <div className="text-center space-y-2 py-4">
          <div className="flex justify-center text-green-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium">Password Reset Email Sent</h3>
          <p className="text-sm text-clustr-darkGray">
            We've sent an email to <span className="font-medium">{email}</span> with instructions to reset your password.
          </p>
          <p className="text-sm text-clustr-darkGray">
            Please check both your inbox and spam folder.
          </p>
        </div>
      )}
      
      {!resetSent ? (
        <Button
          onClick={handleResetPassword}
          className="w-full h-12 bg-clustr-blue hover:bg-blue-600 transition-colors"
          disabled={loading || !email}
        >
          {loading ? "Sending..." : "Send Reset Link"}
        </Button>
      ) : (
        <Button
          onClick={() => {
            if (cooldown === 0) {
              setResetSent(false);
              handleResetPassword();
            }
          }}
          variant="outline"
          className="w-full h-12"
          disabled={cooldown > 0}
        >
          {cooldown > 0 ? `Resend available in ${cooldown}s` : "Resend Reset Link"}
        </Button>
      )}
      
      <div className="text-center mt-2">
        <Button
          variant="link"
          onClick={() => switchToMode("signin")}
          className="text-clustr-text hover:text-clustr-blue transition-colors"
        >
          Back to Sign In
        </Button>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
